import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'

import BillBrandLogo from '@sputnik/components/BillBrandLogo'
import ImageWrapper from '@sputnik/components/ImageWrapper'
import useGetQueryParamsForCreditInit from '@sputnik/pages/PreApplicationPages/useGetQueryParamsForCreditInit'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logInfo } from '@sputnik/utils/loggerUtils'

import InfoTileAndChecklist from './InfoTileAndChecklist'
import styles from './styles.module.scss'

const businessInfoChecklistItems = [
  <FormattedMessage
    key='annual-revenue'
    defaultMessage='Annual revenue'
    id='sputnik.GoodFitResult__16D5H3'
  />,
  <FormattedMessage
    key='business-tax-id'
    defaultMessage='Business tax ID / EIN'
    id='sputnik.GoodFitResult__sgg/zz'
  />,
  <FormattedMessage
    key='industry-type'
    defaultMessage='Industry type'
    id='sputnik.GoodFitResult__TRnC+h'
  />,
]

const financeInfoChecklistItems = [
  <FormattedMessage
    key='expected-monthly-spend'
    defaultMessage='Expected monthly spend'
    id='sputnik.GoodFitResult__dsX8S3'
  />,
  <FormattedMessage
    key='bank-account-log-in'
    defaultMessage='Bank account log in'
    id='sputnik.GoodFitResult__Dwwp/v'
  />,
  <FormattedMessage
    key='bank-routing-account-number'
    defaultMessage='Bank routing & account number'
    id='sputnik.GoodFitResult__j5jkSy'
  />,
]

const authSignerAndCompanyOwnerChecklistItems = [
  <FormattedMessage
    key='social-security-number'
    defaultMessage='Social security number'
    id='sputnik.GoodFitResult__GQ9iTd'
  />,
  <FormattedMessage
    key='residential-address'
    defaultMessage='Residential address'
    id='sputnik.GoodFitResult__4iXhxD'
  />,
  <FormattedMessage
    key='mobile-number'
    defaultMessage='Mobile number'
    id='sputnik.GoodFitResult__JBLdZm'
  />,
]

const GoodFitResult = () => {
  const [getClassName] = useNameFormatter('GoodFitResult')
  const navigate = useNavigate()
  const { search } = useLocation()
  const { shouldNavigateToGetToKnowYou, applicantData, handleInitCreditApplication, isLoadingInitCreditApplication } =
    useGetQueryParamsForCreditInit()

  /* tracking start */
  const { tracker } = useWebTracking()

  useEffect(() => {
    tracker?.track('se_signup__good_fit_success__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  const onLetsGoClick = () => {
    tracker?.track('se_signup__good_fit_success_lets_go__submitted')

    if (shouldNavigateToGetToKnowYou) {
      logInfo('letsGoClick', {
        attributes: {
          dataFromUP: shouldNavigateToGetToKnowYou,
          result: `No data from UP. Redirecting to ${PATHNAME_CONSTS.GET_TO_KNOW_YOU}`,
        },
        eventName: 'onLetsGoClick',
      })
      navigate({ pathname: PATHNAME_CONSTS.GET_TO_KNOW_YOU, search: search || '' }, { replace: true })
    } else {
      logInfo('letsGoClick', {
        attributes: {
          dataFromUP: shouldNavigateToGetToKnowYou,
          result: 'Data found from UP. Attempting to init credit application',
        },
        eventName: 'onLetsGoClick',
      })
      handleInitCreditApplication(applicantData)
    }
  }

  return (
    <>
      <BillBrandLogo />
      <div className={styles[getClassName('')]}>
        <div className={styles[getClassName('text-content')]}>
          <div className={styles[getClassName('heading')]}>
            <FormattedMessage
              defaultMessage='Yay—we’re made for each other! 🎉'
              id='sputnik.GoodFitResult__fLyhDr'
            />
          </div>
          <div className={styles[getClassName('subtitle')]}>
            <FormattedMessage
              defaultMessage="Ready to take our relationship forward? Here's what you'll need to complete the application."
              id='sputnik.GoodFitResult__U48QDE'
            />
          </div>
        </div>
        <div className={styles[getClassName('media-content-container')]}>
          <InfoTileAndChecklist
            checklistItemsArray={businessInfoChecklistItems}
            checklistTitle={
              <FormattedMessage
                defaultMessage='Business info'
                id='sputnik.GoodFitResult__EnfjLD'
              />
            }
            image={
              <ImageWrapper
                alt='business info'
                className={styles[getClassName('')]}
                imageName='good-fit-result-business-info'
              />
            }
          />
          <InfoTileAndChecklist
            checklistItemsArray={financeInfoChecklistItems}
            checklistTitle={
              <FormattedMessage
                defaultMessage='Finance info'
                id='sputnik.GoodFitResult__adRE4f'
              />
            }
            image={
              <ImageWrapper
                alt='finance info'
                className={styles[getClassName('')]}
                imageName='good-fit-result-financial-info'
              />
            }
          />
          <InfoTileAndChecklist
            checklistItemsArray={authSignerAndCompanyOwnerChecklistItems}
            checklistTitle={
              <FormattedMessage
                defaultMessage='Authorized signer'
                id='sputnik.GoodFitResult__2DRSsN'
              />
            }
            image={
              <ImageWrapper
                alt='Authorized signer'
                className={styles[getClassName('')]}
                imageName='good-fit-result-auth-signer'
              />
            }
          />
          <InfoTileAndChecklist
            checklistItemsArray={authSignerAndCompanyOwnerChecklistItems}
            checklistTitle={
              <FormattedMessage
                defaultMessage='Company owners'
                id='sputnik.GoodFitResult__FvqLu2'
              />
            }
            image={
              <ImageWrapper
                alt='company owner'
                className={styles[getClassName('')]}
                imageName='good-fit-result-company-owners'
              />
            }
          />
          <div className={styles[getClassName('button-container')]}>
            <BasicButton
              disabled={isLoadingInitCreditApplication}
              showSpinner={isLoadingInitCreditApplication}
              onClick={onLetsGoClick}
            >
              <FormattedMessage
                defaultMessage="Let's go"
                id='sputnik.GoodFitResult__wVu1FL'
              />
            </BasicButton>
          </div>
        </div>
      </div>
    </>
  )
}

export default GoodFitResult
